import React, { useState, useEffect, useRef } from 'react';
import { testimonials } from '../../data/testimonials';
import { trackEvent } from '../../utils/matomoEvents';

const MAX_TEXT_LENGTH = 150;



export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const progressInterval = useRef(null);
  const slideInterval = useRef(null);
  const SLIDE_DURATION = 15000;
  const PROGRESS_UPDATE_INTERVAL = 50;

  const nextSlide = () => {
    const newIndex = (currentIndex + 1) % testimonials.length;
    setCurrentIndex(newIndex);
    setProgress(0);
    trackEvent('Testimonials', 'NavigateNext', testimonials[newIndex].name, newIndex);
  };

  const prevSlide = () => {
    const newIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
    setCurrentIndex(newIndex);
    setProgress(0);
    trackEvent('Testimonials', 'NavigatePrevious', testimonials[newIndex].name, newIndex);
  };

  useEffect(() => {
    if (!isPaused && !isModalOpen) { // Ensure carousel is paused when modal is open
      progressInterval.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval.current);
            nextSlide(); // Trigger slide change when progress reaches 100
            return 0; // Reset progress
          }
          return prev + (100 * PROGRESS_UPDATE_INTERVAL) / SLIDE_DURATION;
        });
      }, PROGRESS_UPDATE_INTERVAL);
    }

    return () => {
      clearInterval(progressInterval.current);
      clearInterval(slideInterval.current);
    };
  }, [isPaused, isModalOpen]); // Add isModalOpen to dependencies

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const truncateText = (text) => {
    if (text.length <= MAX_TEXT_LENGTH) return text;
    return text.substring(0, MAX_TEXT_LENGTH) + '...';
  };

  const handleCardClick = () => {
    setIsModalOpen(true);
    setIsPaused(true);
    trackEvent('Testimonials', 'ModalOpen', testimonials[currentIndex].name, currentIndex);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsPaused(false);
    trackEvent('Testimonials', 'ModalClose', testimonials[currentIndex].name, currentIndex);
  };

  return (
    <section className="testimonials-section">
      <h2>What Our Families Say</h2>
      <div
        className="testimonials-container"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <button className="nav-button prev" onClick={prevSlide}>‹</button>
        <div className="testimonial-card" onClick={handleCardClick}>
          <div className="rating">{renderStars(testimonials[currentIndex].rating)}</div>
          <p className="testimonial-text">"{truncateText(testimonials[currentIndex].text)}"</p>
          <h3>{testimonials[currentIndex].name}</h3>
          <p className="location">{testimonials[currentIndex].location}</p>
        </div>
        {isModalOpen && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="rating">{renderStars(testimonials[currentIndex].rating)}</div>
              <p>"{testimonials[currentIndex].text}"</p>
              <h3>{testimonials[currentIndex].name}</h3>
              <p className="location">{testimonials[currentIndex].location}</p>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
          </div>
        )}
        <button className="nav-button next" onClick={nextSlide}>›</button>
        <div className="progress-bar">
          <div className="progress" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      <style jsx>{`
        .testimonials-section {
          padding: 4rem 2rem;
          background-color: var(--surface);
          text-align: center;
        }

        h2 {
          font-size: 2.5rem;
          color: var(--text);
          margin-bottom: 2rem;
        }

        .testimonials-container {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          padding: 0 3rem;
        }

        .testimonial-card {
          padding: 2rem;
          background: var(--surface-raised);
          border-radius: 10px;
          box-shadow: 0 2px 8px var(--shadow);
          margin: 0 auto;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .testimonial-card:hover {
          transform: scale(1.02);
        }

        .testimonial-card p {
          font-size: 1.1rem;
          line-height: 1.6;
          color: var(--text-muted);
          margin-bottom: 1rem;
          font-style: italic;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--surface);
          padding: 2rem;
          border-radius: 10px;
          position: relative;
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          z-index: 1001;
        }

        .modal-content p {
          font-size: 1.1rem;
          line-height: 1.6;
          color: var(--text-muted);
          margin-bottom: 1.5rem;
          font-style: italic;
        }
        .close-button {
          position: absolute;
          top: 10px;
          right: 10px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text);
        }

        .close-button:hover {
          color: var(--text);
        }

        .testimonial-card h3 {
          color: var(--text);
          font-size: 1.2rem;
          margin-bottom: 0.5rem;
        }

        .location {
          font-size: 0.9rem;
          color: var(--text-faint);
          font-style: normal !important;
          margin-bottom: 0 !important;
        }

        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.3);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          .rating {
            color: var(--star);
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          cursor: pointer;
          transition: background 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
        }

        .rating {
          color: var(--star);
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .nav-button:hover {
          background: rgba(0, 0, 0, 0.5);
        }

        .prev {
          left: 0;
        }

        .next {
          right: 0;
        }

        .progress-bar {
          width: calc(100% - 6rem);
          height: 4px;
          background: var(--border-soft);
          position: absolute;
          bottom: -8px;
          left: 3rem;
          border-radius: 2px;
          overflow: hidden;
        }

        .progress {
          height: 100%;
          background: var(--text-faint);
          transition: width ${PROGRESS_UPDATE_INTERVAL}ms linear;
        }

        @media (max-width: 768px) {
          .testimonials-section {
            padding: 3rem 1rem;
          }

          h2 {
            font-size: 2rem;
          }

          .testimonial-card {
            padding: 1.5rem;
          }

          .testimonial-card p {
            font-size: 1rem;
          }
        }
      `}</style>
    </section>
  );
}